import { LinkObject } from '@/types/generic';
import { Typography } from '@statsbomb/kitbag-components';
import { Link } from 'react-router-dom';
import { Atom, useAtomValue } from 'jotai';
import { twJoin } from 'tailwind-merge';
import { playerLinkAtom } from '@/atoms/navigation';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { LinkConfig } from '@/types/navigation';
import { ErrorBoundary } from '@sentry/react';
import { TEAM_BASE_ROUTE } from '@/hooks/navigation/useTeamNavigationItem';
import { unwrappedTeamIdMappingAtom } from '@/atoms/queries/teams';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { unwrappedPlayerIdMappingAtom } from '@/atoms/queries/players';
import { PLAYER_BASE_ROUTE } from '@/hooks/navigation/usePlayerNavigationItem';
import { ReactNode } from 'react';
import { sectionAtom } from '@/atoms/section';

const TableLink = ({
  ids,
  label,
  linkConfigAtom,
}: {
  ids: Record<string, string | number>;
  label: LinkObject['label'];
  linkConfigAtom: Atom<LinkConfig>;
}) => {
  const { to, relative } = useGetLinkPath(linkConfigAtom, ids);
  return (
    <Typography as={Link} variant="linkMedium" to={to} relative={relative}>
      {label}
    </Typography>
  );
};

const TableLinkClassic = ({ href, children, disabled }: { href: string; children: ReactNode; disabled?: boolean }) => (
  <Typography
    as="a"
    variant="linkMedium"
    href={href}
    className={twJoin(disabled && 'cursor-default pointer-events-none')}
  >
    {children}
  </Typography>
);

export const TeamLink = ({ id, label }: LinkObject) => {
  const teamsMapping = useAtomValue(unwrappedTeamIdMappingAtom);
  const iqId = teamsMapping[id];

  return (
    <TableLinkClassic href={`${TEAM_BASE_ROUTE}/${iqId}/dashboard`} disabled={!iqId}>
      {label}
    </TableLinkClassic>
  );
};

export const PlayerLink = ({ id, label }: LinkObject) => {
  const isOldPlayerXp = useAtomValue(userNavOverridesAtom).player;
  const section = useAtomValue(sectionAtom);
  const playersMapping = useAtomValue(unwrappedPlayerIdMappingAtom);
  const iqId = playersMapping[id];
  const isPlayerSection = section === 'player';

  return isOldPlayerXp && isPlayerSection ? (
    <TableLinkClassic href={`${PLAYER_BASE_ROUTE}/${iqId}/dashboard`} disabled={!iqId}>
      {label}
    </TableLinkClassic>
  ) : (
    <ErrorBoundary fallback={<Typography variant="linkMedium">{label}</Typography>}>
      <TableLink ids={{ playerId: id }} label={label} linkConfigAtom={playerLinkAtom} />
    </ErrorBoundary>
  );
};
